import React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { KambiBetOffer } from '@mobi/api-types/kambi'
import type { KambiSportName } from '@mobi/api-types/kambi/sports'
import { BetOfferType } from '@mobi/api-types/kambi/constants'
import { getBetOffersByIds } from '@mobi/sports-offering/helpers/queries'
import { SportsIcon } from '@mobi/sports-offering/Components/Common/SportsIcon'
import {
  hasTeamIconForLeague,
  mapLeagueToSportWithNationalFlags,
  hasTeamShieldColorsForLeague,
} from './helpers'
import { TeamIcon } from './Components/TeamIcon'
import { NationalTeamIcon } from './Components/NationalFlagIcon'
import { TeamShieldColorsIcon } from './Components/TeamColorShield'

export const TeamsIcon: React.FC<
  {
    outcomeId: number
    betOfferType: BetOfferType
    betOfferId: KambiBetOffer['id']
    leagueId: TeamsIconSwitchProps['leagueId'] | undefined
  } & Omit<TeamsIconSwitchProps, 'leagueId'>
> = ({
  betOfferId,
  betOfferType,
  outcomeId,
  size,
  sportName,
  leagueId,
  participantId,
  teamName,
}) => {
  if (!leagueId) {
    return <SportsIcon sport={sportName} size={size} />
  }

  if (
    [
      BetOfferType.PlayerLastOccurrence,
      BetOfferType.PlayerOccurrenceLine,
      BetOfferType.PlayerOccurrenceNumber,
    ].includes(betOfferType) &&
    (hasTeamIconForLeague({ leagueId }) || mapLeagueToSportWithNationalFlags({ leagueId }))
  ) {
    return (
      <TeamsIconWithPlayerSupport
        betOfferId={betOfferId}
        outcomeId={outcomeId}
        size={size}
        sportName={sportName}
        leagueId={leagueId}
        teamName={teamName}
        participantId={participantId}
      />
    )
  }

  return (
    <TeamsIconSwitch
      size={size}
      sportName={sportName}
      leagueId={leagueId}
      participantId={participantId}
      teamName={teamName}
    />
  )
}

// ================
// Local Components
// ================

/** For individuals on team, fetch team participant id */
const TeamsIconWithPlayerSupport: React.FC<
  {
    betOfferId: KambiBetOffer['id']
    outcomeId: number
  } & TeamsIconSwitchProps
> = ({ betOfferId, outcomeId, size, leagueId, teamName, sportName }) => {
  const { data, isLoading, isError } = useQuery(getBetOffersByIds([betOfferId]))

  if (isLoading || (!data && !isError)) return null

  const outcome = data?.betOffers[0]?.outcomes.find(o => o.id === outcomeId)
  const participantId = outcome?.eventParticipantId ?? outcome?.participantId

  return participantId ? (
    <TeamsIconSwitch
      participantId={participantId}
      size={size}
      sportName={sportName}
      leagueId={leagueId}
      teamName={teamName}
    />
  ) : (
    <SportsIcon size={size} sport={sportName} />
  )
}

/** Switch through custom team icon, national flag or team colors w/ fallback */
const TeamsIconSwitch: React.FC<{
  size: string
  leagueId: number
  teamName: string | undefined
  participantId?: number | null
  sportName: KambiSportName | undefined
}> = ({ size, leagueId, participantId, sportName, teamName }) => {
  if (participantId && hasTeamIconForLeague({ leagueId })) {
    return <TeamIcon size={size} participantId={participantId} sportName={sportName} />
  }

  const mappedSportWithNationalityFlags = mapLeagueToSportWithNationalFlags({ leagueId })
  if (participantId && mappedSportWithNationalityFlags)
    return (
      <NationalTeamIcon
        size={size}
        sportName={sportName}
        participantId={participantId}
        mappedSportWithNationality={mappedSportWithNationalityFlags}
      />
    )

  const hasTeamShieldColors =
    teamName && hasTeamShieldColorsForLeague({ leagueId: leagueId || 123 })
  if (hasTeamShieldColors)
    return (
      <TeamShieldColorsIcon
        leagueId={leagueId}
        teamName={teamName}
        size={size}
        sportName={sportName}
      />
    )

  return <SportsIcon sport={sportName} size={size} />
}

// ===========
// Local Types
// ===========

type TeamsIconSwitchProps = React.ComponentProps<typeof TeamsIconSwitch>
